import { useState, useEffect } from "react";
import { 
  Settings, 
  Eye, 
  EyeOff, 
  MessageSquare, 
  CreditCard,
  AlertTriangle,
  Save,
  Loader2,
  Check
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const AdminSiteSettings = () => {
  const [settings, setSettings] = useState({
    show_testimonials: false,
    show_pricing_on_home: true,
    maintenance_mode: false,
    custom_announcement: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/site-settings`, { withCredentials: true });
      setSettings(response.data);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, settings, { withCredentials: true });
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-site-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif">Site Settings</h1>
          <p className="text-muted-foreground">Manage global site configuration</p>
        </div>
        <Button onClick={saveSettings} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Testimonials Toggle */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <MessageSquare className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">Testimonials Section</CardTitle>
                <CardDescription>
                  Show or hide the testimonials section on the features page
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {settings.show_testimonials ? (
                  <Eye className="w-5 h-5 text-green-500" />
                ) : (
                  <EyeOff className="w-5 h-5 text-muted-foreground" />
                )}
                <div>
                  <p className="font-medium">
                    {settings.show_testimonials ? "Visible" : "Hidden"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {settings.show_testimonials 
                      ? "Testimonials are shown on the features page"
                      : "Testimonials are hidden from visitors"}
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.show_testimonials}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, show_testimonials: checked }))}
                data-testid="testimonials-toggle"
              />
            </div>
            
            {!settings.show_testimonials && (
              <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-sm text-yellow-400">
                  💡 Tip: Enable testimonials once you have genuine reviews from photographers. 
                  This builds trust with potential customers.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pricing on Homepage */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <CreditCard className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">Pricing Section</CardTitle>
                <CardDescription>
                  Control pricing visibility on the homepage
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Show Pricing on Homepage</p>
                <p className="text-sm text-muted-foreground">
                  Display pricing tiers on the main landing page
                </p>
              </div>
              <Switch
                checked={settings.show_pricing_on_home}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, show_pricing_on_home: checked }))}
              />
            </div>
          </CardContent>
        </Card>

        {/* Maintenance Mode */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/10 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <CardTitle className="text-lg">Maintenance Mode</CardTitle>
                <CardDescription>
                  Temporarily disable public access to the site
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Enable Maintenance Mode</p>
                <p className="text-sm text-muted-foreground">
                  Visitors will see a maintenance page
                </p>
              </div>
              <Switch
                checked={settings.maintenance_mode}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, maintenance_mode: checked }))}
              />
            </div>
            
            {settings.maintenance_mode && (
              <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-sm text-red-400">
                  ⚠️ Maintenance mode is enabled. The public site is inaccessible.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Custom Announcement */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg">Site Announcement</CardTitle>
            <CardDescription>
              Display a banner message across the site (leave empty to hide)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={settings.custom_announcement || ""}
              onChange={(e) => setSettings(prev => ({ ...prev, custom_announcement: e.target.value }))}
              placeholder="e.g., 🎉 New feature: Calendar sync is now available!"
              rows={3}
            />
            {settings.custom_announcement && (
              <div className="mt-4 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-sm font-medium">Preview:</p>
                <p className="text-sm">{settings.custom_announcement}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSiteSettings;
